import {FancyAutocompleteOption} from "./types";
import React, {FC, MouseEvent, useEffect, useMemo, useRef} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import * as fa from "@fortawesome/free-solid-svg-icons";

interface FancyAutocompleteProps {
  label: string
  options: FancyAutocompleteOption[]
  value: string
  onChange: (newValue: string) => void
}

export const FancyAutocomplete: FC<FancyAutocompleteProps> = (props) =>  {
  const [search, setSearch] = React.useState("")
  const [hasFocus, setHasFocus] = React.useState(false)
  const [showAll, setShowAll] = React.useState(false)
  const [selectedPosition, setSelectedPosition] = React.useState<number>(0)

  const input = useRef<HTMLInputElement>(null)

  const optionMap = useMemo(() => {
    return Object.fromEntries(props.options.map(option => [option.key, option]))
  }, [props.options])

  const selectedOptionText = useMemo(() => {
    return optionMap[props.value]?.text ?? ''
  }, [optionMap, props.value])

  const options = useMemo(() => props.options.filter((value) => {
    return value.text.includes(search) || value.subText?.toLowerCase().includes(search.toLowerCase())
  }), [props.options, search])

  const selectOption = (key: string) => {
    setSearch(optionMap[key].text)
    props.onChange(key)
    setHasFocus(false)
    input.current?.blur()
  }

  useEffect(() => {
    if (! options[selectedPosition]) {
      setSelectedPosition(0)
    }
  }, [selectedPosition, options])

  const onItemClick = (event: MouseEvent<HTMLButtonElement>, key: string) => {
    event.preventDefault()
    selectOption(key)
  }
  const onInputFocus = () => {
    setHasFocus(true)
    setSearch("")
  }
  const onInputBlur = () => {
    setHasFocus(false)
  }

  const onInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault()
      if (selectedPosition < options.length - 1) {
        setSelectedPosition(selectedPosition + 1)
      }
    }
    if (event.key === "ArrowUp") {
      event.preventDefault()
      if (selectedPosition > 0) {
        setSelectedPosition(selectedPosition - 1)
      }
    }
    if (event.key === "Enter" || event.key === "Tab") {
      event.preventDefault()
      selectOption(options[selectedPosition].key)
    }
  }

  const hidesSome = options.length > 100 && !showAll
  const sortedOptions = useMemo(() => {
    return [...options].sort((a, b) => {
      if (a.key === "all" || a.key === "-1") return -1
      if (b.key === "all" || b.key === "-1") return 1
      return 0
    })
  }, [options])
  const displayOptions = hidesSome ? sortedOptions.slice(0, 100) : sortedOptions
  return <label className={"flex flex-col text-sm font-medium text-slate-900 w-full max-w-md"}>
    {props.label}
    <div className={"relative mt-1"}>
      <input
        ref={input}
        onFocus={() => onInputFocus()}
        onBlur={() => onInputBlur()}
        onKeyDown={(e) => onInputKeyDown(e)}
        className={"border-2 border-slate-200 outline-slate-700 text-black rounded-xs text-base font-normal px-2 h-10 w-full"}
        onChange={(e) => setSearch(e.target.value)}
        value={hasFocus ? search : selectedOptionText}
      />

      {/* Chevron icon*/}
      <div className={"absolute top-0 right-2 h-full flex items-center justify-end"}>
        <FontAwesomeIcon icon={fa.faChevronDown} className={"text-xs text-slate-900"} />
      </div>

      {hasFocus && <div className={"absolute top-10 left-0 w-full text-black bg-white border border-slate-200 rounded-xs shadow-lg z-10 max-h-[220px] overflow-y-scroll"}>
        <div className={"flex flex-col items-stretch"}>
          {displayOptions.map((value, index) => {
            return <button key={index} type={'button'} className={`h-10 w-full px-4 overflow-x-hidden ${index === selectedPosition ? "bg-slate-100 " : ""} hover:bg-slate-100 ${value.key === props.value ? "text-slate-800" : ''} flex items-center`} onMouseDown={(e) => onItemClick(e, value.key)}>
              {value.leading ? <div className={'w-4 mr-2 flex items-center justify-center'}>{value.leading}</div> : null}
              <div className={"flex flex-col items-start"}>
                <div className={"text-left whitespace-nowrap overflow-hidden"}>{value.text}</div>
                {value.subText ? <div className={"text-xs -mt-1 text-slate-600 whitespace-nowrap overflow-hidden"}>{value.subText}</div> : null}
              </div>
            </button>
          })}
          {hidesSome && <button className={`h-10 w-full px-4 hover:bg-slate-100 text-left`}
                                onMouseDown={() => setShowAll(true)}>
            Toon meer ({options.length - 100})
          </button>}
        </div>
      </div>}
    </div>
  </label>
}
